// Accounts-registry + delivery API (Wave 9.6) — typed functions over the
// admin backend contract. Consumed verbatim; no invented fields.
//
//   GET  /admin/accounts?client_id=&agency_id=&status=&request_id=&q=  — registry list
//   POST /admin/accounts                       — manual add (server re-validates BC link, 422)
//   POST /admin/accounts/validate              — DRY-RUN BC-link check (200 verdict, no writes)
//   POST /admin/accounts/{id}/status           — A7 status (enum + reason handler-enforced)
//   POST /admin/accounts/{id}/whitelist        — provisioning-execution status (NOT proxy
//                                                access); ops-pipeline metadata only
//   POST /admin/accounts/{id}/allowlist        — explicit, audited MERGE of the advertiser id
//                                                into the owning client's PROXY allowlist (D12)
//   GET  /admin/account-requests/{id}          — request + routed agencies + delivered + fulfilment
//   POST /admin/account-requests/{id}/deliver  — batch advertiser-id delivery
//
// The backend answers policy refusals as STRUCTURED bodies:
//   422 {error:"unprocessable", reason, hint, account_id?}   (BC-link enforcement)
//   409 {error:"conflict", status} | {error:"conflict", reason:"already_registered", account_id}
//   409 {error:"conflict", reason:"unbound_client"}          (allowlist merge on an unattributed row)
// Those are expected outcomes the UI renders verbatim, not exceptions — the
// deliver/create helpers convert them into discriminated unions and only throw
// for genuinely unexpected failures.
//
// NAMING: "Provisioning" (accounts.whitelist) is provisioning-execution status
// (NOT proxy access) — ops-pipeline metadata edited via the whitelist route.
// "Allowlist" (this file, everywhere) is strictly the PROXY allowlist — the
// server-side account-ID gate a client is scoped to. The two are deliberately
// distinct.
//
// SECURITY: registering/validating an advertiser id NEVER touches the proxy
// allowlist. The proxy allowlist is only ever written via the EXPLICIT, audited
// merge routes — POST /admin/accounts/{id}/allowlist, or deliver/attribution with
// the opt-in append_allowlist flag (backend D12: never silent).

import { api, type ApiError } from "./client";
import { type FinanceViewFields } from "./financeview";

// One pipeline account row — the backend accountResponse shape. The three
// Wave-9.6 fields are omitempty server-side, so they are optional here.
export interface AccountRow {
  id: string;
  account_id: string;
  agency_id: string;
  client_id: string;
  geo: string;
  request_type: string;
  platform: string;
  whitelist: boolean;
  product_url: string;
  status_account: string;
  email: string;
  requested_at: string;
  shared_at: string;
  bc_share_status: string;
  status_delivery: string;
  account_fee_pct: string;
  created_at: number;
  request_id?: string;
  status_reason?: string;
  status_updated_at?: number;
  // Derived server-side (ALWAYS present): true IFF the row has a non-empty owning
  // client_id AND this account_id is present in that client's PROXY allowlist.
  // false when there is no owning client or the id is not yet on the list —
  // meaning the client cannot see or recharge the account through the proxy.
  in_client_allowlist: boolean;
  // ALWAYS present server-side: a legacy share-created row with NO real
  // advertiser id (account_id=="" && (request_id!="" || bc_share_status
  // =="shared")). Real delivered accounts are always false. Placeholders can
  // only be managed via the JWT row-UUID routes (attribution/unlink below).
  is_placeholder: boolean;
}

// The A7 handler-enforced status enum (the column stays free TEXT server-side,
// so LIST rows may still carry legacy values like "requested").
export const ACCOUNT_STATUSES = [
  "active",
  "whitelisted",
  "paused",
  "banned",
] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

export function statusNeedsReason(status: string): boolean {
  return status === "paused" || status === "banned";
}

// ---- BC-link validation (dry-run verdict + 422 enforcement refusals) --------

export type BCRefusalReason = "not_in_bc" | "balance_not_visible";

export interface BCValidationOk {
  ok: true;
  balance: string;
  currency: string;
  balance_kind: string; // "prepaid" | "budget" | "wallet"
  tiktok_status?: string;
  stale: boolean;
  synced_at?: number;
}

export interface BCValidationRefusal {
  ok: false;
  reason: BCRefusalReason;
  // Present when a bc row exists (balance_not_visible case).
  tiktok_status?: string;
  stale: boolean;
  synced_at?: number;
  // Server remediation hint — present only on a 422 enforcement refusal
  // (the dry-run 200 verdict carries no hint).
  hint?: string;
}

export type BCValidation = BCValidationOk | BCValidationRefusal;

// Dry-run: is this advertiser id linked to the Nexova BC with a readable
// balance? Never mutates anything; 200 always carries the verdict.
export function validateAccount(accountId: string): Promise<BCValidation> {
  return api.post<BCValidation>("/admin/accounts/validate", {
    account_id: accountId,
  });
}

// Convert a thrown 422 {error:"unprocessable", reason, hint} enforcement
// refusal into a BCValidationRefusal, or null when it is not one (caller
// rethrows). The create/update endpoints re-validate server-side and answer
// with exactly this shape.
export function asBCRefusal(err: unknown): BCValidationRefusal | null {
  const e = err as ApiError;
  if (!e || e.status !== 422) return null;
  const b = e.body as { error?: unknown; reason?: unknown; hint?: unknown } | undefined;
  if (!b || b.error !== "unprocessable" || typeof b.reason !== "string") {
    return null;
  }
  return {
    ok: false,
    reason: b.reason as BCRefusalReason,
    stale: false,
    hint: typeof b.hint === "string" ? b.hint : undefined,
  };
}

// ---- Registry list + writes --------------------------------------------------

export interface AccountFilters {
  client_id?: string;
  agency_id?: string;
  status?: string;
  request_id?: string;
  q?: string;
  // Offset pagination (server: parseIntQuery limit/offset).
  limit?: number;
  offset?: number;
}

export function listAccounts(
  filters: AccountFilters = {},
): Promise<{ accounts: AccountRow[] }> {
  const params = new URLSearchParams();
  const { limit, offset, ...rest } = filters;
  for (const [k, v] of Object.entries(rest)) {
    if (v) params.set(k, v);
  }
  if (limit !== undefined) params.set("limit", String(limit));
  if (offset !== undefined) params.set("offset", String(offset));
  const qs = params.toString();
  return api.get<{ accounts: AccountRow[] }>(
    `/admin/accounts${qs ? `?${qs}` : ""}`,
  );
}

// The manual-add body (a subset of the backend accountRequest; unset string
// fields mean NULL server-side).
export interface CreateAccountBody {
  account_id: string;
  client_id?: string;
  agency_id?: string;
  geo?: string;
  product_url?: string;
  platform?: string;
  status_account?: string;
}

// Manual add. Throws for unexpected failures; a 422 BC-link refusal should be
// caught by the caller via asBCRefusal and rendered like a validate failure.
export function createAccount(body: CreateAccountBody): Promise<AccountRow> {
  return api.post<AccountRow>("/admin/accounts", body);
}

// A7 status change; 200 returns the full updated row (incl. status_reason +
// status_updated_at).
export function setAccountStatus(
  id: string,
  status: string,
  reason: string,
): Promise<AccountRow> {
  return api.post<AccountRow>(`/admin/accounts/${id}/status`, {
    status,
    reason,
  });
}

// Provisioning flag toggle (accounts.whitelist — provisioning-execution status,
// NOT proxy access; see addToAllowlist below for the proxy-allowlist merge).
export function setAccountWhitelist(
  id: string,
  whitelist: boolean,
): Promise<{ id: string; whitelist: boolean }> {
  return api.post<{ id: string; whitelist: boolean }>(
    `/admin/accounts/${id}/whitelist`,
    { whitelist },
  );
}

// Explicit, audited merge of the account's advertiser id into its OWNING
// client's PROXY allowlist (backend "account.allowlisted" audit event).
//   POST /admin/accounts/{id}/allowlist  ({id} = the accounts ROW UUID)
// Idempotent server-side (read current list, append if absent, Set). This is
// the D12-compliant single-click grant that lets the client see & recharge the
// account. A row with no owning client_id refuses with
// 409 {reason:"unbound_client"} (thrown as an ApiError — attribute first).
export function addToAllowlist(
  id: string,
): Promise<{ in_client_allowlist: boolean }> {
  return api.post<{ in_client_allowlist: boolean }>(
    `/admin/accounts/${id}/allowlist`,
    {},
  );
}

// The EXPLICIT, audited REMOVAL counterpart to addToAllowlist — drops the
// account's advertiser id from its OWNING client's PROXY allowlist (backend
// "account.deallowlisted" audit event).
//   POST /admin/accounts/{id}/deallowlist  ({id} = the accounts ROW UUID)
// Idempotent server-side (read current list, drop it, Set). This is NEVER auto-
// triggered — it is the only removal affordance besides the ClientDetail
// textarea, so revoking proxy access stays an explicit operator action ("auto-
// grant on link, revoke stays explicit"). A row with no owning client_id refuses
// with 409 {reason:"unbound_client"} (thrown as an ApiError). The owning client
// is resolved SERVER-SIDE from the row, never from request input (invariants
// 2/3/4). 200 -> {in_client_allowlist:false}.
export function deallowlistRow(
  id: string,
): Promise<{ in_client_allowlist: boolean }> {
  return api.post<{ in_client_allowlist: boolean }>(
    `/admin/accounts/${id}/deallowlist`,
    {},
  );
}

// True when a thrown ApiError is the 409 {reason:"unbound_client"} refusal the
// allowlist merge (and append-on-attribution) returns for an unattributed row.
export function isUnboundClient(err: unknown): boolean {
  const e = err as ApiError;
  if (!e || e.status !== 409) return false;
  const b = e.body as { reason?: unknown } | undefined;
  return b?.reason === "unbound_client";
}

// ---- Delivered-row management (attribution + unlink) --------------------------

// Re-attribute an account row to a different agency.
//   POST /admin/accounts/{id}/attribution  ({id} = the accounts ROW UUID)
// 200 -> the updated account JSON. 400 {reason:"unknown_agency"} when the
// agency does not exist (thrown as an ApiError; render its body verbatim).
//
// append_allowlist is DEFAULT-ON on the JWT admin surface (operator-approved
// "auto-grant on link"): the backend merges the account_id into the owning
// client's PROXY allowlist when the field is OMITTED or true — but only when the
// attribution resolves an owning client_id (otherwise a no-op, never an error).
// To OPT OUT the caller must send an EXPLICIT false, so we forward whatever
// boolean it passes (undefined = leave it to the backend default). "Assign once
// → works everywhere"; revoke stays explicit (the deallowlist affordance).
export function setAccountAttribution(
  id: string,
  agencyId: string,
  opts: { append_allowlist?: boolean } = {},
): Promise<AccountRow> {
  const body: Record<string, unknown> = { agency_id: agencyId };
  if (opts.append_allowlist !== undefined) {
    body.append_allowlist = opts.append_allowlist;
  }
  return api.post<AccountRow>(`/admin/accounts/${id}/attribution`, body);
}

// Detach an account row from its request (accounts.request_id -> NULL and any
// account_requests.created_account_id pointing at it -> NULL, atomically).
//   POST /admin/accounts/{id}/unlink   (NO body; idempotent)
// The row is NOT deleted and the request status is NOT recomputed — the
// operator re-delivers if needed. 200 -> the updated account JSON.
export function unlinkAccount(id: string): Promise<AccountRow> {
  return api.post<AccountRow>(`/admin/accounts/${id}/unlink`);
}

// ---- BC live account detail (lazy drawer fetch) ------------------------------

// One spend window from the BC dashboard cache (decimal-string money).
export interface BCSpendWindow {
  spend: string;
  impressions: number;
  clicks: number;
}

// The admin BC dashboard's single-account detail over the bc_* cache — the SAME
// GET /admin/dashboard/accounts/{account_id} the full detail page reads. The
// account hub drawer needs only balance / spend / status / sync-health, so this
// is a deliberately NARROW view of the richer AccountDetailPage shape (extra
// fields the backend returns are simply ignored). SECURITY: this is sanitized
// TikTok RESPONSE data from the server-side cache — never the master token.
export interface BCAccountDetail {
  // Extends FinanceViewFields: the detail body carries the ADR-0054 per-account
  // finance-visibility pair (finance_readable + finance_slots) alongside the
  // balance, so the drawer can say WHY a balance is (un)readable. Optional in
  // the type only — an older backend omits them (see @/api/financeview).
  account: FinanceViewFields & {
    account_id: string;
    name: string;
    display_status: string;
    currency: string;
    balance: string; // decimal string; "" = unknown / unavailable
    synced_at: number;
  };
  spend: Record<string, BCSpendWindow>;
  stale: boolean;
}

// LAZY — call ONLY when the row carries a real advertiser id (account_id !== "").
// The caller must fail SOFT: any error / absent BC row renders "balance
// unavailable" and never blocks the drawer.
export function getAccountBCDetail(accountId: string): Promise<BCAccountDetail> {
  return api.get<BCAccountDetail>(`/admin/dashboard/accounts/${accountId}`);
}

// ---- Account-request detail + delivery ---------------------------------------

// The admin queue row (adminAccountRequestJSON). Status is a free string here
// so an unexpected/new state never breaks rendering.
export interface AccountRequestJSON {
  id: string;
  client_id?: string;
  geo: string;
  request_type: string;
  whitelist_pref?: boolean;
  product_url?: string;
  qty: number;
  notes?: string;
  // The client's TikTok Business Center id or email to share delivered
  // accounts to. Always present in the backend JSON ("" when not captured).
  share_target?: string;
  status: string;
  source: string;
  requested_at?: string;
  assigned_at?: string;
  ready_at?: string;
  shared_at?: string;
  created_at: number;
}

// One routed agency on a request detail. The detail endpoints now return the
// per-agency routing STATUS ("routed" | "declined") alongside the id (an
// agency can decline a request it was routed to). Names resolve client-side
// from /admin/agencies. NOTE: the assign RESPONSE envelope's `agencies` stays a
// flat []string of ids — this richer shape is the request-DETAIL contract only.
export interface RoutedAgency {
  agency_id: string;
  status: "routed" | "declined";
}

export interface AccountRequestDetail {
  request: AccountRequestJSON;
  agencies: RoutedAgency[];
  delivered: AccountRow[];
  total_delivered: number;
  // Effective qty — the backend already applies max(request.qty, 1).
  qty: number;
  fulfilled: boolean;
}

export function getAccountRequestDetail(
  id: string,
): Promise<AccountRequestDetail> {
  return api.get<AccountRequestDetail>(`/admin/account-requests/${id}`);
}

export interface DeliverOk {
  ok: true;
  request: AccountRequestJSON;
  // The rows created by THIS batch only (the detail GET has the full list).
  delivered: AccountRow[];
  total_delivered: number;
  qty: number;
  fulfilled: boolean;
  // True when the batch ALSO appended the delivered advertiser ids to the
  // owning client's PROXY allowlist (the explicit append_allowlist opt-in).
  allowlist_appended?: boolean;
}

// A structured deliver refusal (400 / 409 / 422), rendered verbatim.
export interface DeliverRefusal {
  ok: false;
  status: number;
  error: string; // "bad request" | "conflict" | "unprocessable"
  reason?: string; // "already_registered" | "not_in_bc" | "balance_not_visible"
  account_id?: string; // the offending advertiser id, when per-id
  request_status?: string; // 409 non-deliverable-state variant
  hint?: string; // server remediation hint, verbatim
}

export type DeliverResult = DeliverOk | DeliverRefusal;

function asDeliverRefusal(err: unknown): DeliverRefusal | null {
  const e = err as ApiError;
  if (!e || typeof e.status !== "number") return null;
  if (![400, 409, 422].includes(e.status)) return null;
  const b = e.body as
    | {
        error?: unknown;
        reason?: unknown;
        account_id?: unknown;
        status?: unknown;
        hint?: unknown;
      }
    | undefined;
  if (!b || typeof b.error !== "string") return null;
  return {
    ok: false,
    status: e.status,
    error: b.error,
    reason: typeof b.reason === "string" ? b.reason : undefined,
    account_id: typeof b.account_id === "string" ? b.account_id : undefined,
    request_status: typeof b.status === "string" ? b.status : undefined,
    hint: typeof b.hint === "string" ? b.hint : undefined,
  };
}

// Deliver a batch of REAL TikTok advertiser ids against an assigned /
// in_progress request. All-or-nothing server-side; refusals come back as a
// structured DeliverRefusal for verbatim rendering.
//
// append_allowlist is DEFAULT-ON on the JWT admin surface (operator-approved
// "auto-grant on link"): the backend appends the delivered ids to the owning
// client's PROXY allowlist when the field is OMITTED or true — but only when the
// batch resolves an owning client (otherwise a no-op, never an error). To OPT
// OUT the caller sends an EXPLICIT false. Revoke stays explicit (the deallowlist
// affordance).
export async function deliverAccounts(
  requestId: string,
  body: {
    agency_id?: string;
    account_ids: string[];
    append_allowlist?: boolean;
  },
): Promise<DeliverResult> {
  try {
    const res = await api.post<Omit<DeliverOk, "ok">>(
      `/admin/account-requests/${requestId}/deliver`,
      body,
    );
    return { ok: true, ...res };
  } catch (err) {
    const refusal = asDeliverRefusal(err);
    if (refusal) return refusal;
    throw err;
  }
}

// ---- Share guard --------------------------------------------------------------

// The 409 undelivered-qty guard body: sharing while delivered < qty is refused
// unless the operator forces it (audited server-side with force:true).
export interface ShareUndelivered {
  ok: false;
  reason: "undelivered_qty";
  delivered: number;
  qty: number;
}

export type ShareResult =
  | { ok: true; request: AccountRequestJSON }
  | ShareUndelivered;

// POST /admin/account-requests/{id}/share with the optional {agency_id, force}
// body. Only the structured undelivered_qty 409 becomes a ShareUndelivered —
// every other refusal (403 illegal edge, 409 unbound client / lost race,
// 400 agency not in routed set) still throws for the generic error path.
export async function shareAccountRequest(
  requestId: string,
  opts: { agency_id?: string; force?: boolean } = {},
): Promise<ShareResult> {
  const body: Record<string, unknown> = {};
  if (opts.agency_id) body.agency_id = opts.agency_id;
  if (opts.force) body.force = true;
  try {
    const request = await api.post<AccountRequestJSON>(
      `/admin/account-requests/${requestId}/share`,
      Object.keys(body).length > 0 ? body : undefined,
    );
    return { ok: true, request };
  } catch (err) {
    const e = err as ApiError;
    const b = e?.body as
      | { error?: unknown; reason?: unknown; delivered?: unknown; qty?: unknown }
      | undefined;
    if (
      e?.status === 409 &&
      b?.error === "conflict" &&
      b?.reason === "undelivered_qty" &&
      typeof b.delivered === "number" &&
      typeof b.qty === "number"
    ) {
      return { ok: false, reason: "undelivered_qty", delivered: b.delivered, qty: b.qty };
    }
    throw err;
  }
}

// ---- Reopen (shared -> in_progress) ------------------------------------------

// The 409 fully_delivered guard body: reopening a shared request whose delivered
// count already meets qty is refused — the operator must unlink the placeholder
// first, then re-deliver. Rendered verbatim.
export interface ReopenFullyDelivered {
  ok: false;
  reason: "fully_delivered";
  delivered: number;
  qty: number;
}

export type ReopenResult =
  | { ok: true; request: AccountRequestJSON }
  | ReopenFullyDelivered;

// POST /admin/account-requests/{id}/reopen (admin JWT, no body). A shared but
// UNDER-delivered request goes back to `in_progress` (shared_at preserved, the
// legacy placeholder created_account_id cleared; routed agencies preserved).
// Only the structured 409 fully_delivered becomes a ReopenFullyDelivered — a
// 403 (request not currently shared) or any other refusal still throws for the
// generic error path.
export async function reopenAccountRequest(
  requestId: string,
): Promise<ReopenResult> {
  try {
    const request = await api.post<AccountRequestJSON>(
      `/admin/account-requests/${requestId}/reopen`,
    );
    return { ok: true, request };
  } catch (err) {
    const e = err as ApiError;
    const b = e?.body as
      | { error?: unknown; reason?: unknown; delivered?: unknown; qty?: unknown }
      | undefined;
    if (
      e?.status === 409 &&
      b?.error === "conflict" &&
      b?.reason === "fully_delivered" &&
      typeof b.delivered === "number" &&
      typeof b.qty === "number"
    ) {
      return { ok: false, reason: "fully_delivered", delivered: b.delivered, qty: b.qty };
    }
    throw err;
  }
}
